import React, { useState, useRef, useEffect } from 'react';
import { chatWithAgent, triggerAgent, isConfigured } from '../lib/api';

const AGENTS = [
  { id: 'general',          label: 'Command Center',    icon: '⌘', desc: 'General questions about both businesses', repo: null, agentKey: null },
  { id: 'chief_of_staff',   label: 'Chief of Staff',    icon: '📊', desc: 'Overall performance & strategy', repo: 'meduvita-agents', agentKey: 'chief_of_staff' },
  { id: 'medical_guardian', label: 'Medical Guardian',  icon: '🛡', desc: 'Question QA & NICE compliance', repo: 'meduvita-agents', agentKey: 'medical_guardian' },
  { id: 'retentionist',     label: 'Retentionist',      icon: '💌', desc: 'Nudge emails & retention strategy', repo: 'meduvita-agents', agentKey: 'retentionist' },
  { id: 'marketing_scout',  label: 'Marketing Scout',   icon: '📢', desc: 'Content, competitors & TikTok trends', repo: 'mmf-agents', agentKey: 'marketing_scout' },
  { id: 'ambassador_boss',  label: 'Ambassador Boss',   icon: '🏆', desc: 'MedSoc reps & referral network', repo: 'mmf-agents', agentKey: 'ambassador_boss' },
];

function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: '16px' }}>
      {!isUser && (
        <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: 'var(--accent-m-dim)', border: '1px solid rgba(0,212,170,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', marginRight: '10px', flexShrink: 0 }}>
          {msg.icon || '🤖'}
        </div>
      )}
      <div style={{
        maxWidth: '75%',
        padding: '12px 16px',
        borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
        background: isUser ? 'var(--accent-m-dim)' : 'var(--bg-2)',
        border: `1px solid ${isUser ? 'rgba(0,212,170,0.2)' : 'var(--border)'}`,
        fontSize: '13px',
        color: 'var(--text-2)',
        lineHeight: '1.65',
        whiteSpace: 'pre-wrap',
      }}>
        {msg.content}
        {msg.typing && <span style={{ opacity: 0.5 }}>▊</span>}
      </div>
    </div>
  );
}

export default function AgentChat() {
  const configured = isConfigured();
  const [selectedAgent, setSelectedAgent] = useState(AGENTS[0]);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: `Hey! I'm your Command Center AI. Ask me anything about Meduvita, My Medicine Friend, your agents, data, strategy — anything. What do you need?`, icon: '⌘' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const switchAgent = (agent) => {
    setSelectedAgent(agent);
    setMessages([{
      role: 'assistant',
      content: `Switched to ${agent.label}. ${agent.desc}. What do you want to know?`,
      icon: agent.icon,
    }]);
    setTriggerResult(null);
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    // Add typing indicator
    setMessages(prev => [...prev, { role: 'assistant', content: '', icon: selectedAgent.icon, typing: true }]);

    try {
      if (!configured) {
        // Demo mode — smart local responses
        await new Promise(r => setTimeout(r, 800));
        const demoReplies = {
          'how many users': 'Based on the last morning pulse: 3,847 active users across both platforms, with 142 new signups in the last 24 hours.',
          'agents': 'You have 11 AI agents running: 5 on Meduvita (Chief of Staff, Medical Guardian, Retentionist, Early Warning Scout, Matchmaker) and 2 on MMF (Marketing Scout, Ambassador Boss).',
          'revenue': 'No premium tier yet — that\'s Phase 4. Current focus is user growth. Ambassador network has attributed £1,420 in referred users so far.',
          'default': 'Connect your Cloudflare Worker to get real AI responses. Add REACT_APP_WORKER_URL and REACT_APP_DASHBOARD_SECRET to your Cloudflare Pages environment variables.',
        };
        const key = Object.keys(demoReplies).find(k => input.toLowerCase().includes(k)) || 'default';
        setMessages(prev => prev.slice(0, -1).concat([{ role: 'assistant', content: demoReplies[key], icon: selectedAgent.icon }]));
      } else {
        const historyForApi = newMessages.slice(-10); // last 10 messages for context
        const { reply } = await chatWithAgent(historyForApi, selectedAgent.id);
        setMessages(prev => prev.slice(0, -1).concat([{ role: 'assistant', content: reply, icon: selectedAgent.icon }]));
      }
    } catch (e) {
      setMessages(prev => prev.slice(0, -1).concat([{ role: 'assistant', content: `Error: ${e.message}`, icon: '❌' }]));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const runAgent = async () => {
    if (!selectedAgent.repo || !configured) return;
    setTriggerLoading(true);
    setTriggerResult(null);
    try {
      const result = await triggerAgent(selectedAgent.repo, selectedAgent.agentKey);
      setTriggerResult({ success: true, msg: result.message });
      setMessages(prev => [...prev, { role: 'assistant', content: `✅ ${result.message}\n\nThe agent is now running on GitHub Actions. It will write its results to your Supabase database. Check the Agent Logs page in ~1 minute to see the output.`, icon: selectedAgent.icon }]);
    } catch (e) {
      setTriggerResult({ success: false, msg: e.message });
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ Failed to trigger agent: ${e.message}\n\nMake sure your GITHUB_TOKEN is set in the Worker with "workflow" scope.`, icon: '❌' }]);
    } finally {
      setTriggerLoading(false);
    }
  };

  const quickPrompts = {
    general: ['How many users do we have?', 'What needs my attention today?', 'Give me a business summary'],
    chief_of_staff: ['What happened overnight?', 'Which metric is most concerning?', 'What should I prioritise today?'],
    medical_guardian: ['What questions were flagged?', 'How many questions passed QA today?', 'What NICE guidelines should I review?'],
    retentionist: ['Who are our most at-risk users?', 'How are the nudge emails performing?', 'What day has the best re-engagement rate?'],
    marketing_scout: ['What are today\'s TikTok trends?', 'Did any competitor change pricing?', 'Critique today\'s Reel script'],
    ambassador_boss: ['Who are our top performing reps?', 'Which reps are close to the next tier?', 'How can I recruit more ambassadors?'],
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '20px', height: 'calc(100vh - 80px)' }}>
      {/* Agent selector */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div className="section-label">Talk to an Agent</div>
        {AGENTS.map(agent => (
          <div
            key={agent.id}
            onClick={() => switchAgent(agent)}
            style={{
              padding: '12px 14px',
              borderRadius: '8px',
              cursor: 'pointer',
              border: `1px solid ${selectedAgent.id === agent.id ? 'var(--accent-m)' : 'var(--border)'}`,
              background: selectedAgent.id === agent.id ? 'var(--accent-m-dim)' : 'var(--bg-1)',
              transition: 'all 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
              <span style={{ fontSize: '16px' }}>{agent.icon}</span>
              <span style={{ fontSize: '12px', fontFamily: 'var(--font-display)', fontWeight: '700', color: selectedAgent.id === agent.id ? 'var(--accent-m)' : 'var(--text)' }}>
                {agent.label}
              </span>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-3)', marginLeft: '24px' }}>{agent.desc}</div>
          </div>
        ))}

        {/* Run agent button */}
        {selectedAgent.repo && (
          <div style={{ marginTop: '12px' }}>
            <div className="section-label">Manual Trigger</div>
            <button
              className="btn btn-approve"
              style={{ width: '100%', justifyContent: 'center', padding: '10px' }}
              disabled={triggerLoading || !configured}
              onClick={runAgent}
            >
              {triggerLoading ? '⏳ Triggering...' : `▶ Run ${selectedAgent.label}`}
            </button>
            {!configured && <div style={{ fontSize: '10px', color: 'var(--text-3)', marginTop: '6px', textAlign: 'center' }}>Connect Worker to enable</div>}
            {triggerResult && (
              <div style={{ fontSize: '10px', marginTop: '6px', color: triggerResult.success ? 'var(--green)' : 'var(--red)', textAlign: 'center' }}>
                {triggerResult.success ? '✓ Agent triggered' : '✗ Failed'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat area */}
      <div style={{ display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-2)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '18px' }}>{selectedAgent.icon}</span>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: '800', fontSize: '14px', color: 'var(--text)' }}>{selectedAgent.label}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-3)' }}>
              {configured ? <><span style={{ color: 'var(--green)' }}>● </span>Connected · Powered by Gemini 2.0 Flash</> : <><span style={{ color: 'var(--yellow)' }}>● </span>Demo mode · Connect Worker for real AI</>}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {messages.map((msg, i) => <Message key={i} msg={msg} />)}
          <div ref={bottomRef} />
        </div>

        {/* Quick prompts */}
        <div style={{ padding: '0 20px 12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {(quickPrompts[selectedAgent.id] || []).map((prompt, i) => (
            <button
              key={i}
              onClick={() => { setInput(prompt); inputRef.current?.focus(); }}
              style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '20px', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text-3)', cursor: 'pointer' }}
            >
              {prompt}
            </button>
          ))}
        </div>

        {/* Input */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: '10px' }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
            placeholder={`Ask ${selectedAgent.label} anything...`}
            style={{
              flex: 1, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px',
              padding: '10px 14px', color: 'var(--text)', fontFamily: 'var(--font-mono)', fontSize: '13px',
              outline: 'none',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--accent-m)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}
          />
          <button
            className="btn btn-approve"
            onClick={send}
            disabled={loading || !input.trim()}
            style={{ padding: '10px 18px', flexShrink: 0 }}
          >
            {loading ? '...' : 'Send ↵'}
          </button>
        </div>
      </div>
    </div>
  );
}
